import { API } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import { getIdToken } from './authUtils';
import { filterUnique, flatten, normalizeArray } from './objectUtils';
import { nonEmptyString, normalizeString, toUpperCase } from './typeUtils';

const API_NAME = 'admin-portal-api';

const getConfig = async (body = undefined) => {
    return {
        headers: {
            Authorization: await getIdToken(),
            'X-Correlation-Id': uuidv4(),
        },
        body,
    };
};

export const validateResponse = (resp) => {
    // console.log('CONSUMER_API response:', resp);
    return resp;
};

const handleError = (err) => {
    console.error('CONSUMER_API error:', err);
    throw err;
};

export async function consumerSearch(country, params) {
    const path = `/v1/${country}/consumerSearch`;
    return getConfig(params)
        .then((config) => API.post(API_NAME, path, config))
        .then(validateResponse)
        .then(normalizeConsumerSearch)
        .catch(handleError);
}

export async function consumerGet(country, consumerId) {
    const path = `/v1/${country}/consumer/${consumerId}`;
    return getConfig()
        .then((config) => API.get(API_NAME, path, config))
        .then(validateResponse)
        .then((data) => normalizeConsumerGet(data, country))
        .catch(handleError);
}

export async function consumerUpdate(country, consumerId, params) {
    const path = `/v1/${country}/consumer/${consumerId}`;
    return getConfig(params)
        .then((config) => API.patch(API_NAME, path, config))
        .then(validateResponse)
        .catch(handleError);
}

export async function consumerDelete(country, consumerId) {
    const path = `/v1/${country}/consumer/${consumerId}`;
    return getConfig()
        .then((config) => API.del(API_NAME, path, config))
        .then(validateResponse)
        .catch(handleError);
}

export async function resetPassword(country, params) {
    const path = `/v1/${country}/reset-password`;
    return getConfig(params)
        .then((config) => API.post(API_NAME, path, config))
        .then(validateResponse)
        .catch(handleError);
}

export async function avSet(country, consumerId) {
    const path = `/v1/${country}/av-set/${consumerId}`;
    return getConfig()
        .then((config) => API.get(API_NAME, path, config))
        .then(validateResponse)
        .catch(handleError);
}

export async function getBrandData(country) {
    const path = `/v1/${country}/dictionary?field=consumer.primaryBrand`;
    return getConfig()
        .then((config) => API.get(API_NAME, path, config))
        .then(validateResponse)
        .then(normalizeBrandData)
        .catch(handleError);
}

function normalizeConsumerGet(payload, country) {
    const { data } = payload || {};

    if (data != null) {
        // Normalize Profile
        data.profile = data.profile || {};
        const { profile } = data;

        // Normalize Address
        const defaultAddress = { type: 'personal' };
        profile.address = normalizeArray(profile.address);
        profile.address2 = [defaultAddress, ...profile.address];
        profile.address2 = profile.address2.reduce((result, item) => {
            result[String(item.type).toLowerCase()] = item;
            return result;
        }, {});

        // Normalize Phones
        profile.phones = normalizeArray(profile.phones);
        profile.phones2 = profile.phones.reduce((result, item) => {
            result[String(item.type).toLowerCase()] = item.number;
            return result;
        }, {});

        // Normalize Consents
        data.consents2 = {};
        data.consents2.brands = normalizeArray(
            data?.consents?.brandPreferences?.entitlements
        ).reduce((result, item) => {
            result[String(item).toLowerCase()] = true;
            return result;
        }, {});

        // Normalize HAV
        data.hardAgeVerification = normalizeArray(data.hardAgeVerification);
        data.hav = data.hardAgeVerification.find(
            (item) =>
                item.hardAVCountryRefCode === country &&
                item.hardAVMarketRefCode === `${country}04`
        );

        // Normalize Mobile status
        if (data.mobileVerification?.status) {
            data.mobileVerification.status = toUpperCase(
                data.mobileVerification.status
            );
        }

        // Flatten the result
        payload.data = flatten(data);
    }

    return payload;
}

function normalizeConsumerSearch(payload) {
    const { data } = payload || {};
    if (data != null) {
        payload.data = normalizeArray(data);
        payload.data.forEach((item) => {
            item.profile = item.profile || {};
            item.profile.address = normalizeArray(item.profile.address);
            item.profile.phones = normalizeArray(item.profile.phones);

            // Normalize Address
            const address =
                item.profile.address.find((item) => item.type === 'personal') ||
                item.profile.address[0];
            const { city, addressLine1, addressLine2, postal } = address || {};
            item.profile.address2 = [addressLine2, addressLine1, postal, city]
                .filter((item) => !!item && String(item).trim().length)
                .join(', ');

            // Normalize Phone
            const phone =
                item.profile.phones.find((item) => item.type === 'mobile') ||
                item.profile.phones[0];
            const { number } = phone || {};
            item.profile.phone = number;
        });
    }
    return payload;
}

function normalizeBrandData(payload) {
    const { data } = payload || {};

    const compareByMarket = (a, b) => a.market.localeCompare(b.market);

    const filterNonEmpty = (item) =>
        nonEmptyString(item.id) &&
        nonEmptyString(item.desc) &&
        nonEmptyString(item.market);

    const createDescMap = (result, item) => {
        result[item.desc] = item;
        return result;
    };

    const brands = normalizeArray(data)
        .map((item) => {
            const brand = item.brandFamily;

            const subBrands = normalizeArray(brand.brandSubFamily)
                .map((item2) => ({
                    id: normalizeString(item2.brandSubFamilyID),
                    desc: normalizeString(item2.brandSubFamilyDesc),
                    market: normalizeString(item2.brandSubFamilyDescMarket),
                }))
                .filter(filterNonEmpty)
                .filter(filterUnique((item) => item.desc))
                .sort(compareByMarket);

            const subBrandsMap = subBrands.reduce(createDescMap, {});

            return {
                id: normalizeString(brand.brandFamilyID),
                desc: normalizeString(brand.brandFamilyDesc),
                market: normalizeString(brand.brandFamilyDescMarket),
                subBrands,
                subBrandsMap,
            };
        })
        .filter(filterNonEmpty)
        .filter(filterUnique((item) => item.desc))
        .sort(compareByMarket);

    const brandsMap = brands.reduce(createDescMap, {});

    return { brands, brandsMap };
}
